import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCity } from "@/contexts/Citycontext";
import JobCard from "@/components/JobCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SearchableSelect from "@/components/SearchableSelect";
import PwaPromoBanner from "@/components/PwaPromoBanner";
import {
  Search, MapPin, Briefcase, Building2, Users, Star, ArrowRight,
  Code, Calculator, ShoppingBag, HeartPulse, GraduationCap, Factory,
  UtensilsCrossed, TrendingUp, Truck, ShieldCheck, Sparkles, Store
} from "lucide-react";
import { FaCode, FaCalculator, FaShoppingBag, FaHeartbeat, FaGraduationCap,
         FaIndustry, FaUtensils, FaTruck, FaShieldAlt, FaStore, FaBriefcase,
         FaChartLine } from "react-icons/fa";
import { MdSpa } from "react-icons/md";
import { useAuth } from "@/contexts/AuthContext";

const iconMap = {
  Code: FaCode, Calculator: FaCalculator, ShoppingBag: FaShoppingBag,
  HeartPulse: FaHeartbeat, GraduationCap: FaGraduationCap, Factory: FaIndustry,
  UtensilsCrossed: FaUtensils, TrendingUp: FaChartLine, Truck: FaTruck,
  ShieldCheck: FaShieldAlt, Sparkles: MdSpa, Store: FaStore, Briefcase: FaBriefcase,
};

export default function Home() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("any");
  const [cities, setCities] = useState([]);
  const [trendingCities, setTrendingCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);
  const [stats, setStats] = useState({ jobs: 0, cities: 0, companies: 0, seekers: 0 });
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [bannerImage, setBannerImage] = useState("");
  const navigate = useNavigate();

  // Global city from Navbar context
  const { selectedCity, setSelectedCity } = useCity();

  const { user } = useAuth();
  const [recommended, setRecommended] = useState([]);

  const goPostJob = () => {
    if (user) {
      navigate("/dashboard/poster/new");
    } else {
      // send to login, and (optionally) return here after auth
      navigate("/login", { state: { from: "/dashboard/poster/new" } });
    }
  };

  useEffect(() => {
    if (user?.role === "job_seeker") {
      api.get("/jobs/recommended", { params: { limit: 6 } }).then(r => setRecommended(r.data)).catch(() => setRecommended([]));
    } else {
      setRecommended([]);
    }
  }, [user]);

  useEffect(() => {
    api.get("/cities").then(r => setCities(r.data));
    api.get("/cities/trending?limit=5").then(r => setTrendingCities(r.data)).catch(() => setTrendingCities([]));
    api.get("/categories").then(r => setCategories(r.data));
    api.get("/jobs/stats").then(r => setStats(r.data));
    api.get("/site-settings").then(r => setBannerImage(r.data.banner_image || "")).catch(() => {});
  }, []);

  // Re-fetch featured & latest whenever selectedCity changes
  useEffect(() => {
    const cityQ = selectedCity ? `?city=${encodeURIComponent(selectedCity)}` : "";
    const cityAmp = selectedCity ? `&city=${encodeURIComponent(selectedCity)}` : "";

    api.get(`/jobs/featured${cityQ}`)
      .then(r => setFeatured(r.data))
      .catch(() => setFeatured([]));

    api.get(`/jobs?limit=6${cityAmp}`)
      .then(r => setLatest(r.data.items))
      .catch(() => setLatest([]));
  }, [selectedCity]);

  const search = (e) => {
    e?.preventDefault();
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (selectedCity) p.set("city", selectedCity);
    if (category && category !== "any") p.set("category", category);
    navigate(`/jobs?${p.toString()}`);
  };

  const browseJobsHref = selectedCity
    ? `/jobs?city=${encodeURIComponent(selectedCity)}`
    : "/jobs";

  return (
    <div data-testid="home-page">

      {/* ── HERO ── */}
      <section
        data-testid="hero-banner"
        style={{
          background: bannerImage
            ? `url(${bannerImage}) top center / cover no-repeat`
            : "linear-gradient(135deg, #f8f8f8 0%, #f1f1f1 40%, #fafafa 100%)",
          backgroundAttachment: "scroll",
        }}
        className="relative border-b border-slate-200 overflow-hidden"
      >
        {bannerImage && (
          <>
            <div className="md:hidden absolute inset-0 pointer-events-none"
              style={{ background: "linear-gradient(180deg,rgba(0,0,0,.60) 0%,rgba(0,0,0,.45) 100%)", zIndex: 1 }} />
            <div className="hidden md:block absolute inset-0 pointer-events-none"
              style={{ background: "linear-gradient(135deg,rgba(0,0,0,.55) 0%,rgba(0,0,0,.30) 100%)", zIndex: 1 }} />
          </>
        )}
        {!bannerImage && (
          <>
            <div style={{ position:"absolute", top:"-80px", right:"-80px", width:"360px", height:"360px", borderRadius:"50%", background:"radial-gradient(circle,rgba(0,0,0,.05) 0%,transparent 70%)", pointerEvents:"none" }} />
            <div style={{ position:"absolute", bottom:"-60px", left:"10%", width:"280px", height:"280px", borderRadius:"50%", background:"radial-gradient(circle,rgba(0,0,0,.03) 0%,transparent 70%)", pointerEvents:"none" }} />
          </>
        )}

        {/* ── MOBILE ── */}
        <div className="md:hidden relative z-10 px-4 pt-6 pb-6">

          {/* Active city pill */}
          {selectedCity && (
            <div className={`flex items-center gap-1.5 mb-4 px-3 py-2 rounded-xl text-xs font-medium
              ${bannerImage ? "bg-white/20 text-white" : "bg-black/5 text-slate-700 border border-black/10"}`}>
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              Showing jobs in <span className="font-bold ml-1">{selectedCity}</span>
              <button
                type="button"
                onClick={() => setSelectedCity("")}
                className="ml-auto opacity-60 hover:opacity-100"
              >✕</button>
            </div>
          )}

          <div className="flex items-start justify-between gap-2 mb-5" style={{ minHeight: "160px" }}>
            <div className="flex-1 min-w-0 pt-1">
              <p className={`text-[11px] font-bold tracking-widest uppercase mb-2 ${bannerImage ? "text-white/80" : "text-black"}`}>
                India's #1 Job Platform
              </p>
              <h1
                className={`font-extrabold leading-[1.15] mb-3 ${bannerImage ? "text-white drop-shadow-md" : "text-slate-900"}`}
                style={{ fontSize: "26px", fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                India's upcoming #1 job platform
              </h1>
              <p className={`text-sm leading-snug ${bannerImage ? "text-white/85" : "text-slate-500"}`}>
                Discover 50 lakh+<br />career opportunities
              </p>
            </div>
            {!bannerImage && (
              <div className="shrink-0 self-stretch relative overflow-visible" style={{ width: "38%" }}>
                <div style={{
                  position:"absolute", bottom:"-24px", right:"-16px", width:"145px", height:"190px",
                  borderRadius:"16px 16px 0 0",
                  background:"linear-gradient(145deg,#e5e7eb 0%,#d1d5db 50%,#9ca3af 100%)",
                  display:"flex", alignItems:"flex-end", justifyContent:"center", overflow:"hidden",
                }}>
                  <svg width="120" height="170" viewBox="0 0 120 170" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <ellipse cx="62" cy="30" rx="22" ry="22" fill="#FBBF24"/>
                    <path d="M40 25 Q42 5 62 8 Q82 5 84 25 Q78 15 62 18 Q46 15 40 25Z" fill="#92400E"/>
                    <rect x="56" y="50" width="12" height="12" rx="4" fill="#FBBF24"/>
                    <path d="M30 65 Q38 58 56 60 L64 60 Q82 58 90 65 L95 110 L25 110 Z" fill="#F9FAFB"/>
                    <path d="M56 60 L60 75 L64 60" fill="none" stroke="#E5E7EB" strokeWidth="1.5"/>
                    <path d="M30 68 Q15 80 18 105 L30 100 Q28 85 38 78 Z" fill="#F9FAFB"/>
                    <path d="M90 68 Q105 78 104 100 L92 98 Q95 85 82 76 Z" fill="#F9FAFB"/>
                    <rect x="92" y="78" width="18" height="28" rx="4" fill="#1E293B"/>
                    <rect x="94" y="82" width="14" height="20" rx="2" fill="#6b7280"/>
                    <circle cx="101" cy="92" r="4" fill="white" fillOpacity="0.8"/>
                    <rect x="38" y="110" width="18" height="50" rx="6" fill="#374151"/>
                    <rect x="64" y="110" width="18" height="50" rx="6" fill="#374151"/>
                    <ellipse cx="47" cy="160" rx="12" ry="6" fill="#1E293B"/>
                    <ellipse cx="73" cy="160" rx="12" ry="6" fill="#1E293B"/>
                  </svg>
                </div>
              </div>
            )}
          </div>

          {/* Mobile search trigger */}
          <button
            type="button"
            onClick={() => setMobileSearchOpen(true)}
            className={`w-full flex items-center gap-3 rounded-2xl px-4 h-14 text-left shadow-md mb-4
              ${bannerImage ? "bg-white/95 border border-white/20" : "bg-white border border-slate-200"}`}
            data-testid="hero-search-form-mobile"
          >
            <Search className="w-4 h-4 text-slate-400 shrink-0" />
            <span className="text-sm text-slate-400 flex-1">
              {selectedCity
                ? <><span className="text-slate-700 font-medium">{selectedCity}</span> — search by title</>
                : <>Search jobs by <span className="italic">title</span></>
              }
            </span>
          </button>

          {!bannerImage && (
            <div className="mb-4">
              <p className="text-xs font-semibold text-slate-600 text-center mb-2">Proud to Support</p>
              <div className="flex items-center justify-center gap-5">
                {[["GOI","Ministry of Labour","& Employment"],["NCVT","NCVT","Certified"],["DPIIT","DPIIT","#startupindia"]].map(([abbr,l1,l2]) => (
                  <div key={abbr} className="flex items-center gap-1.5">
                    <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                      <span className="text-[9px] font-bold text-slate-700">{abbr}</span>
                    </div>
                    <div>
                      <p className="text-[9px] font-semibold text-slate-700 leading-tight">{l1}</p>
                      <p className="text-[9px] text-slate-500 leading-tight">{l2}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className={`text-xs font-medium text-center mb-4 ${bannerImage ? "text-white/80" : "text-slate-600"}`}>
            Trusted by <span className={`font-bold ${bannerImage ? "text-white" : "text-slate-900"}`}>1000+ enterprises</span>
            {" "}and <span className={`font-bold ${bannerImage ? "text-white" : "text-slate-900"}`}>7 lakh+ MSMEs</span> for hiring
          </p>

          <div className="flex gap-2 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden">
            {categories.slice(0, 8).map(cat => (
              <button key={cat.id} type="button"
                onClick={() => {
                  const p = new URLSearchParams();
                  p.set("category", cat.name);
                  if (selectedCity) p.set("city", selectedCity);
                  navigate(`/jobs?${p.toString()}`);
                }}
                className={`shrink-0 px-3 py-1.5 rounded-full border text-xs font-medium transition whitespace-nowrap
                  ${bannerImage
                    ? "border-white/60 bg-white/15 text-white hover:bg-white/25"
                    : "border-slate-300 bg-white text-slate-600 hover:border-black hover:text-black"}`}
              >{cat.name}</button>
            ))}
          </div>
        </div>

        {/* ── DESKTOP ── */}
        <div className="hidden md:block max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10 w-full">
          <p className={`text-xs font-bold tracking-widest mb-3 ${bannerImage ? "text-white/80" : "text-black"}`}>
            Post Your #1'st Job For Free
          </p>
          <h1
            className={`text-6xl font-extrabold leading-tight mb-3 ${bannerImage ? "text-white" : "text-slate-900"}`}
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            India's upcoming #1 <br/>job platform
          </h1>
          <p className={`text-lg mb-10 ${bannerImage ? "text-white/85" : "text-slate-500"}`}>
            Discover 50 lakh+ career opportunities across India — completely free.
          </p>

          {/* ── DESKTOP SEARCH BAR ── */}
          <form onSubmit={search} data-testid="hero-search-form"
            className="flex items-stretch bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">

            {/* Keyword */}
            <div className="flex items-center gap-2 px-5 flex-1 min-w-0 border-r border-slate-100">
              <Search className="w-4 h-4 text-slate-400 shrink-0" />
              <Input
                data-testid="hero-search-input"
                placeholder="Search jobs by title, skill or company..."
                value={q}
                onChange={e => setQ(e.target.value)}
                className="border-0 focus-visible:ring-0 shadow-none h-14 text-sm p-0"
              />
            </div>

            {/* Category */}
            <div className="flex items-center px-4 border-r border-slate-100 w-52 shrink-0">
              <SearchableSelect
                data-testid="hero-category-select"
                icon={Briefcase}
                value={category}
                onChange={setCategory}
                clearValue="any"
                clearLabel="Any Category"
                searchPlaceholder="Search category..."
                options={categories.map(c => ({ value: c.name, label: c.name }))}
                triggerClassName="h-14 py-0 text-sm"
                panelClassName="left-0"
              />
            </div>

            {/* City — synced with context */}
            <div className="flex items-center px-4 border-r border-slate-100 w-48 shrink-0">
              <SearchableSelect
                data-testid="hero-city-select"
                icon={MapPin}
                value={selectedCity || "any"}
                onChange={v => setSelectedCity(v === "any" ? "" : v)}
                clearValue="any"
                clearLabel="Any City"
                searchPlaceholder="Search city..."
                options={cities.map(c => ({ value: c.name, label: c.name, count: c.job_count }))}
                triggerClassName="h-14 py-0 text-sm"
                panelClassName="left-0"
              />
            </div>

            <Button type="submit" data-testid="hero-search-btn"
              className="h-auto px-8 rounded-none bg-black hover:bg-slate-800 text-white text-sm font-semibold">
              Search Jobs
            </Button>
          </form>

          {/* Selected city hint below search bar */}
          {selectedCity && (
            <p className={`mt-3 text-xs flex items-center gap-1.5 ${bannerImage ? "text-white/70" : "text-slate-500"}`}>
              <MapPin className="w-3 h-3" />
              Filtering by <span className={`font-semibold ${bannerImage ? "text-white" : "text-slate-800"}`}>{selectedCity}</span>
              <button type="button" onClick={() => setSelectedCity("")}
                className="ml-1 underline opacity-70 hover:opacity-100">Clear</button>
            </p>
          )}

          <p className={`mt-5 text-xs font-medium ${bannerImage ? "text-white/70" : "text-slate-400"}`}>
            Trusted by{" "}
            <span className={bannerImage ? "text-white font-semibold" : "text-slate-600 font-semibold"}>1000+ enterprises</span>
            {" "}and{" "}
            <span className={bannerImage ? "text-white font-semibold" : "text-slate-600 font-semibold"}>7 lakh+ MSMEs</span>
            {" "}for hiring
          </p>
        </div>

        {/* ── Mobile Search Modal ── */}
        {mobileSearchOpen && (
          <div className="md:hidden fixed inset-0 z-50 flex flex-col"
            style={{ background: "rgba(0,0,0,0.4)" }} data-testid="mobile-search-modal">
            <div className="bg-white rounded-b-3xl overflow-hidden shadow-2xl">
              <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
                <button type="button" onClick={() => setMobileSearchOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500">←</button>
                <span className="text-sm font-semibold text-slate-700">Search Jobs</span>
              </div>
              <form onSubmit={e => { search(e); setMobileSearchOpen(false); }} className="p-4 flex flex-col gap-3">
                <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 h-13">
                  <Search className="w-4 h-4 text-slate-400 shrink-0" />
                  <Input autoFocus placeholder="Search jobs by title" value={q}
                    onChange={e => setQ(e.target.value)}
                    className="border-0 focus-visible:ring-0 shadow-none bg-transparent h-12 p-0 text-sm" />
                </div>
                <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 h-13">
                  <SearchableSelect
                    icon={Briefcase}
                    value={category}
                    onChange={setCategory}
                    clearValue="any"
                    clearLabel="Any Category"
                    searchPlaceholder="Search category..."
                    options={categories.map(c => ({ value: c.name, label: c.name }))}
                    triggerClassName="h-12 py-0 text-sm bg-transparent"
                  />
                </div>
                {/* City — synced with context */}
                <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 h-13">
                  <SearchableSelect
                    icon={MapPin}
                    value={selectedCity || "any"}
                    onChange={v => setSelectedCity(v === "any" ? "" : v)}
                    clearValue="any"
                    clearLabel="Any City"
                    searchPlaceholder="Search city..."
                    options={cities.map(c => ({ value: c.name, label: c.name, count: c.job_count }))}
                    triggerClassName="h-12 py-0 text-sm bg-transparent"
                  />
                </div>
                <Button type="submit"
                  className="w-full h-12 rounded-xl bg-black hover:bg-slate-800 text-white text-sm font-semibold mt-1"
                  data-testid="hero-search-btn-mobile">
                  Search Jobs
                </Button>
              </form>
            </div>
            <div className="flex-1" onClick={() => setMobileSearchOpen(false)} />
          </div>
        )}
      </section>

{/* ── Post a Job Banner ── */}
<section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
  <div className="rounded-2xl bg-black text-white px-6 py-6 md:px-10 md:py-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
    <div className="flex items-center gap-4">
      <div>
        <h3 className="text-lg md:text-2xl font-bold tracking-tight">Post Your Job Now</h3>
        <p className="text-white/60 text-sm mt-1">
          Reach thousands of candidates — your first post is free.
        </p>
      </div>
    </div>
    <Button
      onClick={goPostJob}
      data-testid="banner-post-job-btn"
      className="bg-white text-black hover:bg-slate-200 font-semibold h-11 rounded-full transition w-full sm:w-auto sm:px-7"
    >
      Post Job <ArrowRight className="w-4 h-4 ml-2" />
    </Button>
  </div>
</section>

      {/* ── Stats ── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <h2 className="text-xl font-bold text-slate-900">Platform Overview</h2>
          <p className="text-sm text-slate-500">Real-time performance metrics and talent acquisition insights for the last 30 days.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: Briefcase, label: "Active Jobs", value: stats.jobs },
            { icon: MapPin, label: "Global Cities", value: stats.cities },
            { icon: Building2, label: "Verified Employers", value: stats.companies },
            { icon: Users, label: "Active Job Seekers", value: stats.seekers },
          ].map((s, i) => (
            <div key={i}
              className="rounded-xl bg-white border border-slate-200 p-6 flex flex-col items-center justify-center text-center gap-3"
              data-testid={`stat-${s.label.toLowerCase().replace(/ /g,'-')}`}>
              <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 grid place-items-center text-black">
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{s.value}</div>
                <div className="text-xs text-slate-500">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Recommended for you (job seekers only) ── */}
      {user?.role === "job_seeker" && recommended.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5 text-[#1D6FF2]" />
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Recommended for you</h2>
          </div>
          <p className="text-slate-600 mb-6">Based on your profile, saved jobs, applications, and alerts.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {recommended.map(j => <JobCard key={j.id} job={j} />)}
          </div>
        </section>
      )}

      {/* ── Categories ── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex justify-between items-end mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Browse by Category</h2>
            <p className="text-slate-600 mt-1">Pick a field that matches your skills.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {categories.slice(0, 12).map(cat => {
            const Icon = iconMap[cat.icon] || Briefcase;
            return (
              <Link key={cat.id}
                to={`/jobs?category=${encodeURIComponent(cat.name)}${selectedCity ? `&city=${encodeURIComponent(selectedCity)}` : ""}`}
                data-testid={`cat-${cat.name}`}
                className="group rounded-xl border border-slate-200 bg-white p-5 hover:shadow-md hover:border-black/40 transition-all flex flex-col items-center text-center">
                <div className="w-11 h-11 rounded-lg bg-slate-100 grid place-items-center text-slate-800 group-hover:bg-black group-hover:text-white transition">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="mt-4 font-semibold text-slate-900">{cat.name}</h3>
                <p className="text-xs text-slate-500 mt-1">{cat.job_count} jobs</p>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ── App install promo ── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <PwaPromoBanner />
      </section>

      {/* ── Featured ── */}
      {featured.length > 0 && (
        <section className="bg-slate-50 border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
                  <Star className="w-6 h-6 fill-black text-black" /> Featured Jobs
                </h2>
                <p className="text-slate-600 mt-1">
                  {selectedCity
                    ? <>Hand-picked openings in <span className="font-semibold text-slate-900">{selectedCity}</span>.</>
                    : "Hand-picked openings from trusted employers."
                  }
                </p>
              </div>
              <Link to={browseJobsHref} className="text-sm text-black font-medium hidden md:inline-flex items-center gap-1 hover:underline">
                View all <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {featured.map(j => <JobCard key={j.id} job={j} />)}
            </div>
          </div>
        </section>
      )}

      {/* ── Browse by City ── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-1">Browse by City</h2>
        <p className="text-slate-600 mb-8">Local jobs across India — find work near you.</p>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
          {/* Top 5 trending cities — blend of most jobs posted & most searched */}
          {trendingCities.map(c => (
            <button
              key={c.id}
              type="button"
              onClick={() => setSelectedCity(selectedCity === c.name ? "" : c.name)}
              data-testid={`city-${c.name}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm font-medium transition
                ${selectedCity === c.name
                  ? "bg-black text-white border-black"
                  : "border-slate-200 bg-white hover:border-black hover:text-black text-slate-700"
                }`}
            >
              <TrendingUp className={`w-3.5 h-3.5 ${selectedCity === c.name ? "text-white/70" : "text-slate-400"}`} />
              {c.name}
              <span className={`ml-0.5 ${selectedCity === c.name ? "text-white/70" : "text-slate-400"}`}>({c.job_count})</span>
            </button>
          ))}

          {/* Every other city, via a searchable dropdown */}
          <div className="w-full sm:w-56">
            <SearchableSelect
              icon={MapPin}
              value={selectedCity || "any"}
              onChange={v => setSelectedCity(v === "any" ? "" : v)}
              clearValue="any"
              clearLabel="More cities..."
              searchPlaceholder="Search city..."
              options={cities.map(c => ({ value: c.name, label: c.name, count: c.job_count }))}
              triggerClassName="h-10 px-4 rounded-full border border-slate-200 bg-white hover:border-black"
            />
          </div>
        </div>
      </section>

      {/* ── Latest Openings ── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Latest Openings</h2>
            <p className="text-slate-600 mt-1">
              {selectedCity
                ? <>Fresh jobs in <span className="font-semibold text-slate-900">{selectedCity}</span>.</>
                : "Fresh jobs added recently."
              }
            </p>
          </div>
          <Link to={browseJobsHref} className="text-sm text-black font-medium hover:underline">See all jobs →</Link>
        </div>
        {latest.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {latest.map(j => <JobCard key={j.id} job={j} />)}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-center py-16 bg-white border border-slate-200 rounded-xl">
            <MapPin className="w-10 h-10 text-slate-300 mb-3" />
            <h3 className="text-lg font-semibold text-slate-700">
              {selectedCity ? `No jobs found in ${selectedCity}` : "No jobs available"}
            </h3>
            <p className="text-slate-400 text-sm mt-1">
              {selectedCity ? "Try selecting a different city." : "Check back soon."}
            </p>
            {selectedCity && (
              <button type="button" onClick={() => setSelectedCity("")}
                className="mt-4 text-sm font-medium underline text-slate-600 hover:text-black">
                Show all cities
              </button>
            )}
          </div>
        )}
      </section>

      {/* ── CTA ── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl p-8 bg-black text-white relative overflow-hidden">
          <h3 className="text-2xl font-bold">Post a Job — It's Free</h3>
          <p className="mt-2 text-white/80 max-w-md">Hire from your city in minutes. Local shops, businesses and companies welcome.</p>
          <Link to="/register">
            <Button className="mt-6 bg-white text-black hover:bg-slate-100 border border-white/20" data-testid="cta-post-job">
              Post a Job →
            </Button>
          </Link>
        </div>
        <div className="rounded-2xl p-8 bg-slate-900 text-white">
          <h3 className="text-2xl font-bold">Looking for a Job?</h3>
          <p className="mt-2 text-white/80 max-w-md">Register, build your profile, and apply to thousands of openings — free forever.</p>
          <Link to="/register">
            <Button className="mt-6 bg-white text-slate-900 hover:bg-slate-100" data-testid="cta-find-job">
              Register Now →
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}